import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import { CircularProgress, Typography, useTheme } from '@mui/material';
import { FC, useState } from 'react';
import Dropzone from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { uploadFiles } from 'src/api/csApi';

interface Props {
  disabled?: boolean;
  imageUploaded: (url: string, name: string) => void;
  fieldname?: string;
}

export const UploadDropzone: FC<Props> = ({ disabled, fieldname, imageUploaded }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [isLoading, setIsLoading] = useState(false);
  const handleDrop = async (dropped: File[]) => {
    try {
      if (dropped.length > 0) {
        setIsLoading(true);
        const file = dropped[0];
        const formData = new FormData();
        formData.append('file', file);
        const resp = await uploadFiles(formData);
        imageUploaded(resp.data.data.url, file.name);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dropzone
      disabled={disabled}
      accept={{
        'image/*': [],
      }}
      onDrop={handleDrop}
      noKeyboard
      multiple={false}
    >
      {({ getRootProps, getInputProps }) => (
        <div
          {...getRootProps()}
          style={{
            position: 'relative',
            textAlign: 'center',
            padding: '55px 0',
            minHeight: 120,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            border: `1px dashed ${theme.palette.grey[400]}`,
            borderRadius: 4,
            cursor: disabled ? 'unset' : 'pointer',
          }}
        >
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'row',
            }}
          >
            {isLoading ? (
              <CircularProgress color="primary" />
            ) : (
              <>
                <ImageOutlinedIcon
                  fontSize="small"
                  sx={{
                    color: disabled ? theme.palette.grey[400] : theme.palette.primary.main,
                    marginRight: 1,
                  }}
                />
                <Typography color={disabled ? 'text.disabled' : 'text.primary'} variant="subtitle2">
                  {t('components.uploaddropzone.text')}
                </Typography>
              </>
            )}
          </div>
          <input data-cy={fieldname} id="image-upload" {...getInputProps()} />
        </div>
      )}
    </Dropzone>
  );
};
